import React from 'react';
import type { BasePlacement } from '@popperjs/core';

import Tooltip from '../../Tooltip/Tooltip';
import { Variant } from '../../Tooltip/types';
import { StyledInfoFilledIcon } from './InfoLabel.styled';

interface InfoLabelProps {
  /**
   * Text displayed in the tooltip
   */
  tooltipText: string;
  /**
   * Preferable location of the tooltip if there is space available. It can be one of the following:
   * `Tooltip.Position.TOP`,`Tooltip.Position.BOTTOM`, `Tooltip.Position.RIGHT`, `Tooltip.Position.LEFT`.
   * More information at [Popper.js](https://popper.js.org/react-popper/v2/render-props/#placement).
   */
  tooltipPlace?: BasePlacement;
  /**
   * dataCy for the tooltip
   */
  tooltipDataCy?: string;
  /**
   * dataCy for the icon
   */
  infoDataCy?: string;
  /**
   * Node in which the tooltip will be rendered via react portal.
   */
  renderReferencePortalNode?: HTMLElement;
  /**
   * Area where the boundary of the tooltip is verified.
   * More information at [Popper.js](https://popper.js.org/docs/v2/utils/detect-overflow/#boundary)
   */
  boundary?: HTMLElement;
  /**
   * List of placements that the tooltip will use if there is no space available in the preferable location,
   * it will try in order and use the first location where it finds space.
   * More information at [Popper.js](https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements)
   */
  fallbackPlacements?: BasePlacement[];
  /**
   * Variant on which the style of the icon depends. Options are: `InfoLabel.Variant.DARK`, `InfoLabel.Variant.LIGHT`
   */
  variant?: Variant;
}

const InfoLabel = ({
  tooltipText,
  tooltipPlace,
  tooltipDataCy,
  infoDataCy,
  renderReferencePortalNode,
  boundary,
  fallbackPlacements,
  variant = Variant.DARK,
}: InfoLabelProps) => {
  return (
    <Tooltip
      text={tooltipText}
      fallbackPlacements={fallbackPlacements || [tooltipPlace || Tooltip.Position.TOP]}
      placement={tooltipPlace}
      dataCy={tooltipDataCy}
      renderReferencePortalNode={renderReferencePortalNode}
      boundary={boundary}
      variant={variant}
    >
      <StyledInfoFilledIcon data-cy={infoDataCy} $variant={variant} />
    </Tooltip>
  );
};

InfoLabel.TooltipPlace = Tooltip.Position;
InfoLabel.Variant = Variant;

export default InfoLabel;
